import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from './AuthContext';
import { apiRequest } from '../utils/api';
import Cookies from 'js-cookie';
import { getStoredSsoSession } from '../authService';
import { auth } from '../firebase';

const UserDataContext = createContext();

export function UserDataProvider({ children }) {
  const { getToken, loading: authLoading, user } = useAuth();

  // Estados para los datos
  const [userData, setUserData] = useState(null);
  const [empresaData, setEmpresaData] = useState(null);
  const [usuariosEmpresa, setUsuariosEmpresa] = useState([]);

  // Estados de carga
  const [loading, setLoading] = useState(true);
  const [userDataLoading, setUserDataLoading] = useState(true);
  const [empresaDataLoading, setEmpresaDataLoading] = useState(true);
  const [usuariosEmpresaLoading, setUsuariosEmpresaLoading] = useState(true);

  // Estados de error
  const [error, setError] = useState(null);

  // Referencias para comparar datos y evitar actualizaciones innecesarias
  const userDataRef = useRef(null);
  const empresaDataRef = useRef(null);
  const usuariosEmpresaRef = useRef([]);

  // Función para comparar objetos profundamente
  const deepEqual = useCallback((obj1, obj2) => {
    if (obj1 === obj2) return true;
    if (obj1 == null || obj2 == null) return false;
    if (typeof obj1 !== 'object' || typeof obj2 !== 'object') return obj1 === obj2;

    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) return false;

    for (const key of keys1) {
      if (!keys2.includes(key)) return false;
      if (!deepEqual(obj1[key], obj2[key])) return false;
    }

    return true;
  }, []);

  // Función para comparar arrays
  const arraysEqual = useCallback((arr1, arr2) => {
    if (arr1.length !== arr2.length) return false;
    return arr1.every((item, index) => deepEqual(item, arr2[index]));
  }, [deepEqual]);

  const getSsoContextData = useCallback(() => {
    const session = getStoredSsoSession();
    if (!session) return null;

    const empresaId = session.empresa?.id || Number(session.empresaId) || session.empresaId;
    const empresa = session.empresa || null;

    return {
      userData: {
        nombre: empresa?.nombre || 'Usuario SSO',
        apellido: '',
        email: empresa?.email || '',
        empresaId,
        rol: 'admin',
        activo: true,
        sso: true,
        partnerId: session.partnerId,
      },
      empresaData: empresa,
      empresaId,
    };
  }, []);

  // Función para obtener datos del usuario
  const fetchUserData = useCallback(async (forceRefresh = false) => {
    try {
      const ssoData = getSsoContextData();
      const hasFirebaseUser = Boolean(auth.currentUser) || (user && !user.isSso);

      if (ssoData && !hasFirebaseUser) {
        if (forceRefresh || !deepEqual(ssoData.userData, userDataRef.current)) {
          userDataRef.current = ssoData.userData;
          setUserData(ssoData.userData);
        }

        Cookies.set('empresaId', String(ssoData.empresaId));
        localStorage.setItem('empresaId', String(ssoData.empresaId));

        if (ssoData.empresaData && (forceRefresh || !deepEqual(ssoData.empresaData, empresaDataRef.current))) {
          empresaDataRef.current = ssoData.empresaData;
          setEmpresaData(ssoData.empresaData);
          Cookies.set('empresaData', JSON.stringify(ssoData.empresaData));
          localStorage.setItem('empresaData', JSON.stringify(ssoData.empresaData));
        }

        return ssoData.userData;
      }

      const token = await getToken();
      if (!token) {
        setError('No se pudo obtener el token de autenticación');
        return null;
      }

      const response = await apiRequest('/datos-usuario');

      if (!response.ok) {
        throw new Error('Error al obtener datos del usuario');
      }

      const data = await response.json();
      const firebaseUidActual = auth.currentUser?.uid ?? null;

      if (
        firebaseUidActual &&
        data.firebase_uid &&
        firebaseUidActual !== data.firebase_uid
      ) {
        console.error('[UserData] El usuario de Firebase no coincide con /datos-usuario', {
          firebaseUidActual,
          firebase_uidApi: data.firebase_uid,
          userIdApi: data.id,
        });
      }

      console.info('[Sesión] Usuario según /datos-usuario:', {
        id: data.id,
        nombre: data.nombre,
        documento: data.documento,
        genero: data.genero,
        firebase_uid: data.firebase_uid,
        firebaseUidActual,
      });

      // Solo actualizar si los datos realmente cambiaron
      if (forceRefresh || !deepEqual(data, userDataRef.current)) {
        userDataRef.current = data;
        setUserData(data);

        // Guardar userId en cookies si existe
        if (data.id) {
          Cookies.set('userId', data.id);
          localStorage.setItem('userId', data.id);
        }

        if (data.firebase_uid) {
          localStorage.setItem('firebaseUid', data.firebase_uid);
        }

        // Si hay empresaId, obtener datos de la empresa
        const empresaId = data.empresaId || Cookies.get('empresaId') || localStorage.getItem('empresaId');
        if (empresaId && (!empresaDataRef.current || empresaDataRef.current.id !== empresaId)) {
          fetchEmpresaData(empresaId, forceRefresh);
          fetchUsuariosEmpresa(empresaId, forceRefresh);
        }
      }

      return data;
    } catch (error) {
      console.error('Error al obtener datos del usuario:', error);
      setError(error.message);
      return null;
    }
  }, [getToken, deepEqual, getSsoContextData, user]);

  // Función para obtener datos de la empresa
  const fetchEmpresaData = useCallback(async (empresaId, forceRefresh = false) => {
    if (!empresaId) return null;

    const ssoData = getSsoContextData();
    if (ssoData?.empresaData && String(ssoData.empresaId) === String(empresaId)) {
      if (forceRefresh || !deepEqual(ssoData.empresaData, empresaDataRef.current)) {
        empresaDataRef.current = ssoData.empresaData;
        setEmpresaData(ssoData.empresaData);
      }

      return ssoData.empresaData;
    }

    // Si ya tenemos los datos de esta empresa y no es un refresh forzado, no hacer nada
    if (!forceRefresh && empresaDataRef.current && String(empresaDataRef.current.id) === String(empresaId)) {
      return empresaDataRef.current;
    }

    try {
      const token = await getToken();
      if (!token) {
        return null;
      }

      const response = await apiRequest(`/datos-empresa/${empresaId}`);

      if (!response.ok) {
        throw new Error('No se pudo obtener los datos de la empresa');
      }

      const data = await response.json();

      // Solo actualizar si los datos realmente cambiaron
      if (forceRefresh || !deepEqual(data, empresaDataRef.current)) {
        empresaDataRef.current = data;
        setEmpresaData(data);
      }

      return data;
    } catch (error) {
      console.error('Error al obtener los datos de la empresa:', error);
      return null;
    }
  }, [getToken, deepEqual, getSsoContextData]);

  // Función para obtener usuarios de la empresa
  const fetchUsuariosEmpresa = useCallback(async (empresaId, forceRefresh = false) => {
    if (!empresaId) return [];

    // Si ya tenemos los datos de esta empresa y no es un refresh forzado, no hacer nada
    if (!forceRefresh && usuariosEmpresaRef.current.length > 0) {
      // Verificar que los usuarios pertenecen a la misma empresa
      const primeraEmpresaId = usuariosEmpresaRef.current[0]?.empresaId;
      if (primeraEmpresaId === empresaId) {
        return usuariosEmpresaRef.current;
      }
    }

    try {
      const token = await getToken();
      if (!token) {
        return [];
      }

      const response = await apiRequest(`/usuarios-empresa/${empresaId}`);

      if (!response.ok) {
        throw new Error('Error al obtener los usuarios de la empresa');
      }

      const data = await response.json();
      const usuariosArray = Array.isArray(data) ? data : [];

      // Solo actualizar si los datos realmente cambiaron
      if (forceRefresh || !arraysEqual(usuariosArray, usuariosEmpresaRef.current)) {
        usuariosEmpresaRef.current = usuariosArray;
        setUsuariosEmpresa(usuariosArray);
      }

      return usuariosArray;
    } catch (error) {
      console.error('Error al obtener los usuarios de la empresa:', error);
      return [];
    }
  }, [getToken, arraysEqual]);

  // Función para obtener retiros (con filtros, no se cachea directamente)
  const fetchRetiros = useCallback(async (empresaId, mes, año, forceRefresh = false) => {
    if (!empresaId || !mes || !año) {
      return [];
    }

    try {
      const token = await getToken();
      if (!token) {
        return [];
      }

      const response = await apiRequest(`/retiros-empresa?empresaId=${empresaId}&mes=${mes}&anio=${año}`);

      if (!response.ok) {
        throw new Error('Error al obtener los retiros');
      }

      const data = await response.json();
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('Error al obtener los retiros:', error);
      return [];
    }
  }, [getToken]);

  // Función para obtener multas (con filtros, no se cachea directamente)
  const fetchMultas = useCallback(async (empresaId, mes, año, forceRefresh = false) => {
    if (!empresaId || !mes || !año) {
      return null;
    }

    try {
      const token = await getToken();
      if (!token) {
        return null;
      }

      const response = await apiRequest(`/multas/empresa/${empresaId}?mes=${mes}&anio=${año}`);

      if (!response.ok) {
        // 404 es válido (no hay multas)
        if (response.status === 404) {
          return null;
        }
        throw new Error('Error al obtener las multas');
      }

      const data = await response.json();
      return Array.isArray(data) && data.length > 0 ? data[0] : null;
    } catch (error) {
      console.error('Error al obtener las multas:', error);
      return null;
    }
  }, [getToken]);

  // Función para obtener datos de un usuario específico por ID
  const fetchUsuarioById = useCallback(async (userId, forceRefresh = false) => {
    if (!userId) return null;

    try {
      const token = await getToken();
      if (!token) {
        return null;
      }

      const response = await apiRequest(`/datos-usuario-id/${userId}`);

      if (!response.ok) {
        throw new Error('Error al obtener los datos del usuario');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error al obtener los datos del usuario:', error);
      return null;
    }
  }, [getToken]);

  // Función para refrescar todos los datos
  const refreshAll = useCallback(async () => {
    setLoading(true);
    try {
      const userDataResult = await fetchUserData(true);
      const empresaId = userDataResult?.empresaId || Cookies.get('empresaId') || localStorage.getItem('empresaId');

      if (empresaId) {
        await Promise.allSettled([
          fetchEmpresaData(empresaId, true),
          fetchUsuariosEmpresa(empresaId, true)
        ]);
      }
    } finally {
      setLoading(false);
    }
  }, [fetchUserData, fetchEmpresaData, fetchUsuariosEmpresa]);

  // Función para actualizar datos del usuario después de una modificación
  const updateUserData = useCallback((updatedData) => {
    if (!deepEqual(updatedData, userDataRef.current)) {
      userDataRef.current = updatedData;
      setUserData(updatedData);
    }
  }, [deepEqual]);

  // Función para actualizar usuarios de la empresa después de una modificación
  const updateUsuariosEmpresa = useCallback((updatedUsuarios) => {
    const usuariosArray = Array.isArray(updatedUsuarios) ? updatedUsuarios : [];
    if (!arraysEqual(usuariosArray, usuariosEmpresaRef.current)) {
      usuariosEmpresaRef.current = usuariosArray;
      setUsuariosEmpresa(usuariosArray);
    }
  }, [arraysEqual]);

  // Cargar datos iniciales cuando el usuario esté autenticado
  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (!user) {
      // Si no hay usuario, limpiar todos los datos
      setUserData(null);
      setEmpresaData(null);
      setUsuariosEmpresa([]);
      userDataRef.current = null;
      empresaDataRef.current = null;
      usuariosEmpresaRef.current = [];
      setLoading(false);
      setUserDataLoading(false);
      setEmpresaDataLoading(false);
      setUsuariosEmpresaLoading(false);
      return;
    }

    // Cargar todos los datos en paralelo
    const loadAllData = async () => {
      setLoading(true);
      setUserDataLoading(true);
      setEmpresaDataLoading(true);
      setUsuariosEmpresaLoading(true);
      setError(null);

      try {
        const userDataResult = await fetchUserData();

        if (userDataResult) {
          setUserDataLoading(false);

          // Obtener empresaId de diferentes fuentes
          const empresaId = userDataResult.empresaId || Cookies.get('empresaId') || localStorage.getItem('empresaId');

          if (empresaId) {
            // Cargar datos de empresa y usuarios en paralelo
            const [empresaResult, usuariosResult] = await Promise.allSettled([
              fetchEmpresaData(empresaId),
              fetchUsuariosEmpresa(empresaId)
            ]);

            if (empresaResult.status === 'fulfilled') {
              setEmpresaDataLoading(false);
            } else {
              console.error('Error al cargar datos de empresa:', empresaResult.reason);
              setEmpresaDataLoading(false);
            }

            if (usuariosResult.status === 'fulfilled') {
              setUsuariosEmpresaLoading(false);
            } else {
              console.error('Error al cargar usuarios de empresa:', usuariosResult.reason);
              setUsuariosEmpresaLoading(false);
            }
          } else {
            setEmpresaDataLoading(false);
            setUsuariosEmpresaLoading(false);
          }
        } else {
          setUserDataLoading(false);
          setEmpresaDataLoading(false);
          setUsuariosEmpresaLoading(false);
        }
      } catch (error) {
        console.error('Error general al cargar datos:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    loadAllData();
  }, [user, authLoading, fetchUserData, fetchEmpresaData, fetchUsuariosEmpresa]);

  // Valores memoizados para el contexto
  const value = useMemo(() => ({
    // Datos
    userData,
    empresaData,
    usuariosEmpresa,

    // Estados de carga
    loading,
    userDataLoading,
    empresaDataLoading,
    usuariosEmpresaLoading,

    // Error
    error,

    // Funciones
    fetchUserData,
    fetchEmpresaData,
    fetchUsuariosEmpresa,
    fetchRetiros,
    fetchMultas,
    fetchUsuarioById,
    refreshAll,
    updateUserData,
    updateUsuariosEmpresa
  }), [
    userData,
    empresaData,
    usuariosEmpresa,
    loading,
    userDataLoading,
    empresaDataLoading,
    usuariosEmpresaLoading,
    error,
    fetchUserData,
    fetchEmpresaData,
    fetchUsuariosEmpresa,
    fetchRetiros,
    fetchMultas,
    fetchUsuarioById,
    refreshAll,
    updateUserData,
    updateUsuariosEmpresa
  ]);

  return (
    <UserDataContext.Provider value={value}>
      {children}
    </UserDataContext.Provider>
  );
}

// Typechecking props for the UserDataProvider
UserDataProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useUserData() {
  const context = useContext(UserDataContext);
  if (!context) {
    throw new Error('useUserData debe usarse dentro de UserDataProvider');
  }
  return context;
}

